import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, OnInit } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { catchError, Observable, of, switchMap, throwError, map } from 'rxjs';
import { User } from '../user/user.types';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class AuthService{
    [x: string]: any;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private apiOnboarding = 'https://apibea.azurewebsites.net/api';
    private apiOnboardingV2 = 'https://apibea-v2-preview.azurewebsites.net/api/v2';
    private authToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJkYW5pZWxAaW5pY2lhYmxvZy5jb20iLCJlbWFpbCI6ImRhbmllbEBpbmljaWFibG9nLmNvbSIsImp0aSI6IjUwZTY0YWY1LWQwMTMtNDA4YS05Yjk2LTBjNWNiZTk1ZTI1NiIsIm5iZiI6MTU1OTMwMzEwNiwiaWF0IjoiNS8zMS8yMDE5IDExOjQ1OjA2IEFNIiwiZXhwIjoxNjUzOTExMTA2LCJpc3MiOiJodHRwczovL2FwaWJlYS5henVyZXdlYnNpdGVzLm5ldCIsImF1ZCI6Imh0dHBzOi8vYXBpYmVhLmF6dXJld2Vic2l0ZXMubmV0In0.jjaDTDAwWjdkQFP1HpLPIC2jImibmwukEddowT2Gn5I';
    private registerToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJqb25heUBzdXBwb3J0Ymxncy5jb20iLCJlbWFpbCI6ImpvbmF5QHN1cHBvcnRibGdzLmNvbSIsImp0aSI6IjRkZTI4NWFkLWU5YmMtNDUxYy04NDJmLWRiMzJkMDdmMDFjYSIsIm5iZiI6MTc0MjI5NDU1MiwiaWF0IjoiMy8xOC8yMDI1IDEwOjQyOjMyIEFNIiwiZXhwIjoxODM2OTAyNTUyLCJpc3MiOiJodHRwczovL2FwaWJlYS1wcmV2aWV3LmF6dXJld2Vic2l0ZXMubmV0IiwiYXVkIjoiaHR0cHM6Ly9hcGliZWEtcHJldmlldy5henVyZXdlYnNpdGVzLm5ldCJ9.ljx7cEl74v4QLFrKXgZ1U8DIikdUpG1u99Pl9EViPWk';
    rememberMe : boolean;
    _authenticated: boolean = false;
    
    /**
     * Constructor
     */
    constructor(
        private _router: Router
    ) {
        // Verificar si hay tokens almacenados en la sesión o el localStorage
        this._loadStoredToken();
    }

    // Método privado para cargar el token almacenado
    private _loadStoredToken(): void {
        // Primero intentamos obtener el token del sessionStorage (usado cuando no hay rememberMe)
        const sessionToken = sessionStorage.getItem('onboardingAccessToken');
        
        // Si no hay token en sessionStorage, intentamos con localStorage
        const localToken = localStorage.getItem('onboardingAccessToken');
        
        // Verificar si el token en sessionStorage fue guardado explícitamente con rememberMe=false
        if (sessionToken) {
            // Si el token está en sessionStorage pero no hay explícitamente un indicador de rememberMe=false,
            // asumimos que es una recarga de página y debemos limpiarlo
            const rememberMeFlag = sessionStorage.getItem('rememberMe');
            
            if (rememberMeFlag === 'false') {
                // Es una sesión que no debe persistir en recargas (F5)
                // Limpiamos todo y redirigimos al login
                this._accessToken = null;
                this._authenticated = false;
                sessionStorage.removeItem('onboardingAccessToken');
                sessionStorage.removeItem('user');
                sessionStorage.removeItem('rememberMe');
                
                // Esperar un momento para que los cambios se apliquen y luego redirigir
                setTimeout(() => {
                    this._router.navigate(['/sign-in']);
                }, 100);
            }
        } 
        else if (localToken) {
            this._accessToken = localToken;
            this._authenticated = true;
            this.rememberMe = true;
            
            // También cargamos el usuario desde localStorage si existe
            const userStr = localStorage.getItem('user');
            if (userStr) {
                try {
                    this._userService.user = JSON.parse(userStr);
                } catch (e) {
                    console.error('Error parsing user data from localStorage', e);
                }
            }
        }
    }
    
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        this._accessToken = token;
        
        // Guardar el token en el almacenamiento correspondiente según rememberMe
        if (this.rememberMe) {
            localStorage.setItem('onboardingAccessToken', token);
        } else {
            sessionStorage.setItem('onboardingAccessToken', token);
            // Asegurar que no queden restos en localStorage
            localStorage.removeItem('onboardingAccessToken');
        }
    }

    get accessToken(): string {
        // Si ya tenemos el token en memoria, usarlo
        if (this._accessToken) {
            return this._accessToken;
        }
        
        // Si no, intentar obtenerlo de sessionStorage primero (caso de no rememberMe)
        const sessionToken = sessionStorage.getItem('onboardingAccessToken');
        if (sessionToken) {
            this._accessToken = sessionToken;
            return sessionToken;
        }
        
        // Si no está en sessionStorage, intentar con localStorage
        const localToken = localStorage.getItem('onboardingAccessToken');
        if (localToken) {
            this._accessToken = localToken;
            return localToken;
        }
        
        // Si no encontramos el token en ningún almacenamiento
        return '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(credentials : {usermail: string}): Observable<any> {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/json'
        });
    
        return this._httpClient.post(`${this.apiOnboardingV2}/account/RecoveryAccountPassword`, credentials, { headers }).pipe(
          catchError(error => {
            // Log del error para diagnóstico
            console.error('Error en recuperación de contraseña:', error);
            
            // No redireccionamos automáticamente en caso de error
            // Dejamos que el componente maneje la visualización del error
            return throwError(() => ({
              status: error.status,
              message: error.error?.message || 'Ocurrió un error al recuperar la contraseña',
              originalError: error
            }));
          })
        );
      }

    /**
     * Recupera la contraseña de una cuenta usando el token de restablecimiento
     *
     * @param data Objeto con ForgotKey (token), nueva contraseña y URL de solicitud
     * @returns Observable con la respuesta del servidor o error
     */
    recoveryPasswordFromAccount(data: { ForgotKey: string; NewPassword: string; RequestURL: string }): Observable<any> {
        // Validamos que los datos necesarios estén presentes
        if (!data.ForgotKey || !data.NewPassword || !data.RequestURL) {
            return throwError(() => new Error('Datos de recuperación de contraseña incompletos'));
        }

        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        return this._httpClient.post(`${this.apiOnboardingV2}/users/RecoveryPasswordFromAccount`, data, { headers }).pipe(
            catchError((error) => {
                console.error('Error en recuperación de contraseña:', error);
                
                // Manejo específico para tipos de error conocidos
                if (error.status === 500) {
                    return throwError(() => ({
                        status: error.status,
                        message: error.error?.message || 'Error interno del servidor al recuperar la contraseña',
                        originalError: error
                    }));
                }
                
                // Para cualquier otro error
                return throwError(() => ({
                    status: error.status || 0,
                    message: error.error?.message || 'Error al recuperar la contraseña',
                    originalError: error
                }));
            })
        );
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     * @param credentials
     */
    signIn(credentials: { email: string; password: string; captcha: string }): any {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'appkey': '6148C8DA-0C19-4472-84EC-747C9503A0CE'
        });
    
        // Realizar la llamada HTTP para el inicio de sesión
        return this._httpClient.post(`${this.apiOnboardingV2}/account/login`, credentials, { headers }).pipe(
            map((response: any) => {
                // Verificar que el rol del usuario sea "OnBoarding"
                if (response && response.rol && response.rol !== 'OnBoarding') {
                    // Lanzar un error observable para que sea capturado como un error HTTP
                    return throwError(() => ({
                        error: true,
                        status: 403,
                        message: 'Acceso denegado: solo usuarios con rol "OnBoarding" pueden iniciar sesión en esta plataforma'
                    }));
                }
                // Si el rol es correcto, devolvemos la respuesta normal
                return response;
            }),
            // Aplanar el Observable anidado que se crea cuando lanzamos throwError
            switchMap((response) => {
                if (response instanceof Observable) {
                    return response;
                }
                return of(response);
            }),
            catchError((error: HttpErrorResponse) => {
                // Manejo de errores del API
                console.error('Error interceptado en servicio de autenticación:', error);
                return throwError(() => error);
            })
        );
    }
    
    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient
            .post('api/auth/sign-in-with-token', {
                accessToken: this.accessToken,
            })
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap((response: any) => {
                    // Replace the access token with the new one if it's available on
                    // the response object.
                    //
                    // This is an added optional step for better security. Once you sign
                    // in using the token, you should generate a new one on the server
                    // side and attach it to the response object. Then the following
                    // piece of code can replace the token with the refreshed one.
                    if (response.accessToken) {
                        this.accessToken = response.accessToken;
                    }

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;

                    // Return true
                    return of(true);
                })
            );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from all storage locations
        localStorage.removeItem('onboardingAccessToken');
        localStorage.removeItem('user');
        sessionStorage.removeItem('onboardingAccessToken');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('rememberMe');
        
        // Set the authenticated flag to false
        this._authenticated = false;
        
        // Clear the access token from memory
        this._accessToken = null;
        
        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        confirmPassword: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    register(registerData: {Name: string, Email: string, Password: string, LanguageId: number}): Observable<any> {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${this.registerToken}`,
            'apikey': '9FEB3227-B92E-4825-B1DD-A50E3A60A67A',
            'appkey': '34B57314-AEA4-4298-9935-199010D97DF1',
            'Content-Type': 'application/json',
        });
        
        // Asegurarnos de que el formato coincida exactamente con lo esperado
        const payload = {
            Name: registerData.Name,
            Email: registerData.Email,
            Password: registerData.Password,
            LanguageId: registerData.LanguageId
        };
        
        return this._httpClient.post(`${this.apiOnboardingV2}/users/CreateUserOnboarding`, payload, { headers }).pipe(
            catchError((error) => {
                console.error('Error en registro:', error);
                // Si el error es 500 pero relacionado con correo duplicado, manejarlo adecuadamente
                if (error.status === 500) {
                    // Verificar si hay algún mensaje específico sobre correo duplicado
                    if (error.error && typeof error.error === 'string' && error.error.toLowerCase().includes('duplicate')) {
                    }
                    return of(false); // Devolvemos false de manera controlada
                }
                return throwError(() => error);
            })
        );
    }

    confirmUserOnboarding(confirmationData: {Email: string}): Observable<any> {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${this.registerToken}`,
            'apikey': '9FEB3227-B92E-4825-B1DD-A50E3A60A67A',
            'appkey': '34B57314-AEA4-4298-9935-199010D97DF1',
            'Content-Type': 'application/json',
        });

        // El método funciona igual que en el registro inicial, enviando solo el Email
        const payload = { Email: confirmationData.Email };
        
        return this._httpClient.post(`${this.apiOnboardingV2}/users/ConfirmUserOnboarding`, payload, { headers }).pipe(
            catchError((error) => {
                console.error('Error en confirmUserOnboarding:', error);
                // Propagamos el error para que el componente lo maneje
                return throwError(() => error);
            })
        );
    }

    confirmCodeOnboarding(confirmationData: {onboardingId: string, userInput: number}): Observable<any> {
        const headers = new HttpHeaders({
            'apikey': '9FEB3227-B92E-4825-B1DD-A50E3A60A67A',
            'appkey': '34B57314-AEA4-4298-9935-199010D97DF1',
            'Content-Type': 'application/json',
        });
        // Crear el payload para la petición POST
        const payload = {
            onboardingId: confirmationData.onboardingId,
            userInput: confirmationData.userInput
        };

        return this._httpClient.post(`${this.apiOnboardingV2}/users/ConfirmCodeOnboarding`, payload, { headers }).pipe(
            catchError((error) => {
                console.error('Error en confirmCodeOnboarding:', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Inicia sesión automáticamente después de confirmar el código
     * 
     * @param email Email del usuario recién confirmado
     * @param password Password del usuario
     */
    signInAfterConfirmation(email: string, password: string): Observable<any> {
        // Crear credenciales sin captcha para autenticación interna después de confirmación
        const credentials = {
            email: email,
            password: password,
            captcha: 'auto-login-after-confirmation' // Valor especial que indica inicio de sesión automático
        };

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'appkey': '6148C8DA-0C19-4472-84EC-747C9503A0CE'
        });
    
        // Realizar la llamada HTTP para el inicio de sesión
        return this._httpClient.post(`${this.apiOnboardingV2}/account/login`, credentials, { 
            headers,
            observe: 'response'
        }).pipe(
            catchError((error: HttpErrorResponse) => {
                console.error('Error en inicio de sesión automático:', error);
                return throwError(() => error);
            }),
            map((response: any) => {
                // Extraer el cuerpo de la respuesta
                const body = response.body;
                
                if (body && body.accessToken) {
                    // Configurar el estado autenticado
                    this._authenticated = true;
                    
                    // Crear el objeto de usuario - comprobando que todos los campos existan
                    const user = {
                        guid: body.onBoardingId || body.invGuid || '',
                        name: body.name || email.split('@')[0], // Fallback al nombre de usuario del email
                        email: body.userEmail || email,
                        avatar: body.profileURLImage || '',
                        apiKey: body.apiKey || ''
                    };
                    
                    // Verificar que el GUID exista
                    if (!user.guid) {
                        console.warn('No se encontró GUID en la respuesta del login:', body);
                    }
                    
                    // Almacenar token y datos de usuario
                    this.accessToken = body.accessToken;
                    localStorage.setItem('onboardingAccessToken', body.accessToken);
                    localStorage.setItem('user', JSON.stringify(user));
                    
                    // Establecer el usuario en el servicio de usuario
                    this._userService.user = user;
                    
                    return {
                        success: true,
                        redirectURL: 'signed-in-redirect',
                        user: user
                    };
                } else {
                    return {
                        success: false,
                        message: 'No se recibió el token de acceso'
                    };
                }
            })
        );
    }
}
